import { useMemo } from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Storefront, Plus, Envelope, ArrowRight } from "@phosphor-icons/react";

const fetcher = (url) => api.get(url).then((r) => r.data);

// Inline Amazon smile-arrow logo (simpleicons removed Amazon for trademark
// reasons, so we render the canonical svg path ourselves).
const AmazonLogo = ({ size = 48 }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill="#FF9900"
        aria-label="Amazon"
    >
        <path d="M.045 18.02c.072-.116.187-.124.348-.022 3.636 2.11 7.594 3.166 11.87 3.166 2.852 0 5.668-.533 8.447-1.595l.315-.14c.138-.06.234-.1.293-.13.226-.088.39-.046.525.13.12.174.09.336-.12.48-.256.19-.6.41-1.006.66-1.244.743-2.64 1.316-4.185 1.726a17.617 17.617 0 01-4.83.615c-2.74 0-5.317-.477-7.728-1.444-2.4-.97-4.554-2.32-6.46-4.05-.107-.093-.16-.18-.16-.258 0-.04.024-.083.07-.13zm6.265-6.79c0-1.01.25-1.87.748-2.582.5-.71 1.19-1.25 2.062-1.61.798-.34 1.78-.59 2.95-.74 1.16-.16 2.7-.27 4.62-.36V5.3c0-1.31-.16-2.18-.46-2.61-.31-.46-.86-.7-1.65-.7l-.21.02c-.92.07-1.42.46-1.51 1.16-.02.16-.06.27-.13.32-.04.05-.13.07-.22.09l-2.75-.34c-.16-.04-.27-.11-.27-.27 0-.05.01-.1.03-.16C9.79.91 11.96 0 15.04 0c.85 0 1.71.1 2.59.32.88.21 1.62.5 2.21.85.59.36 1.07.79 1.47 1.29.4.5.69 1.05.86 1.65.18.6.27 1.24.27 1.94v7.07c0 .5.07.96.21 1.37.14.42.27.71.41.88l.22.31c.07.11.11.21.11.31 0 .11-.05.21-.16.31-1.69 1.47-2.6 2.25-2.74 2.34-.21.12-.43.13-.66.05-.18-.18-.34-.32-.49-.45a8.9 8.9 0 01-.51-.56l-.43-.5-.42-.51c-1.15 1.25-2.28 2.03-3.39 2.34-.7.21-1.55.31-2.57.31-1.57 0-2.86-.48-3.86-1.44-1.01-.97-1.51-2.33-1.51-4.1zm4.42-.51c0 .78.2 1.42.59 1.9.39.48.92.72 1.59.72.06 0 .14-.01.25-.02.11-.01.18-.02.21-.02.86-.23 1.52-.78 1.99-1.66.23-.39.4-.83.51-1.31.12-.49.18-.88.19-1.19a45 45 0 00.02-1.27v-.68a16.07 16.07 0 00-2.82.31c-1.69.49-2.53 1.56-2.53 3.22zm12.86 8.16c.04-.06.11-.13.21-.21.6-.41 1.18-.69 1.72-.83.91-.23 1.79-.36 2.66-.39.23-.02.46.01.66.07.99.09 1.59.25 1.79.49.09.13.13.34.13.61v.24c0 .8-.22 1.74-.66 2.83-.43 1.08-1.04 1.96-1.81 2.62-.12.1-.21.14-.3.14-.05 0-.09-.01-.13-.02-.13-.06-.16-.18-.09-.36.85-1.98 1.27-3.36 1.27-4.14 0-.24-.05-.42-.13-.54-.21-.24-.79-.36-1.74-.36-.35 0-.76.03-1.23.07-.51.06-.97.12-1.4.18-.12 0-.2-.02-.24-.05-.04-.04-.05-.07-.04-.11 0-.06.02-.11.07-.16h.06z"/>
    </svg>
);

// Each supplier we wire into ScanBox. `iconSlug` matches the simpleicons.org
// brand identifier (https://simpleicons.org/) — the CDN returns the official
// logo as a tinted SVG, no asset hosting required.
const SUPPLIERS = [
    {
        key: "amazon",
        label: "Amazon",
        tagline: "Commandes amazon.fr",
        iconNode: <AmazonLogo size={44} />,
        color: "FF9900",
        bg: "#FFF7E6",
        importPath: "/parametres",
        settingsPath: "/parametres",
    },
    {
        key: "ovh",
        label: "OVHcloud",
        tagline: "Factures hébergement & domaines",
        iconSlug: "ovh",
        color: "123F6D",
        bg: "#E8EEF6",
        importPath: "/parametres",
        settingsPath: "/parametres",
    },
    {
        key: "divers",
        label: "Connecteur mail",
        tagline: "Pièces jointes PDF depuis IMAP",
        // No simple-icons slug for a generic mailbox; render the Phosphor envelope.
        iconNode: <Envelope size={48} weight="duotone" />,
        color: "0F766E",
        bg: "#E6FFFB",
        importPath: "/connecteur-mail",
        settingsPath: "/connecteur-mail",
    },
];

function SupplierLogo({ supp }) {
    if (supp.iconNode) {
        return (
            <div className="h-16 w-16 rounded-lg flex items-center justify-center"
                 style={{ background: supp.bg, color: `#${supp.color}` }}>
                {supp.iconNode}
            </div>
        );
    }
    return (
        <div className="h-16 w-16 rounded-lg flex items-center justify-center p-3"
             style={{ background: supp.bg }}>
            <img
                src={`https://cdn.simpleicons.org/${supp.iconSlug}/${supp.color}`}
                alt={supp.label}
                className="max-h-full max-w-full"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
        </div>
    );
}

function SupplierCard({ supp, status }) {
    const configured = !!status?.configured;
    return (
        <Card className="ec-card hover:shadow-md transition-shadow" data-testid={`supplier-card-${supp.key}`}>
            <CardContent className="p-5 flex flex-col gap-4 h-full">
                <div className="flex items-start gap-4">
                    <SupplierLogo supp={supp} />
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            <h3 className="font-display text-lg font-bold tracking-tight truncate">
                                {supp.label}
                            </h3>
                            {configured ? (
                                <Badge variant="outline" className="text-emerald-700 border-emerald-200 text-xs">
                                    <CheckCircle size={11} weight="fill" className="mr-1" />
                                    Connecté
                                </Badge>
                            ) : (
                                <Badge variant="outline" className="text-muted-foreground text-xs">
                                    Non connecté
                                </Badge>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{supp.tagline}</p>
                        {configured && status?.email && (
                            <p className="text-xs text-muted-foreground mt-1 truncate" title={status.email}>
                                {status.email}{status.folder ? ` · ${status.folder}` : ""}
                            </p>
                        )}
                    </div>
                </div>
                <div className="mt-auto flex items-center gap-2">
                    {configured ? (
                        <Button asChild className="bg-primary hover:bg-primary/90 flex-1"
                                data-testid={`supplier-import-${supp.key}`}>
                            <Link to={supp.importPath}>
                                Lancer un import
                                <ArrowRight size={14} className="ml-1" />
                            </Link>
                        </Button>
                    ) : (
                        <Button asChild variant="outline" className="flex-1"
                                data-testid={`supplier-configure-${supp.key}`}>
                            <Link to={supp.settingsPath}>
                                <Plus size={14} className="mr-1" />
                                Configurer
                            </Link>
                        </Button>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

export default function Suppliers() {
    const { data: amzn } = useSWR("/amazon/credentials", fetcher);
    const { data: creds } = useSWR("/credentials", fetcher);

    const statusByKey = useMemo(() => {
        const out = {};
        if (amzn?.configured) out.amazon = { configured: true, email: amzn.email };
        for (const c of creds?.items || []) {
            if (c.configured && c.provider !== "amazon") {
                out[c.provider] = {
                    configured: true,
                    email: c.email,
                    folder: c.folder,
                };
            }
        }
        return out;
    }, [amzn, creds]);

    const configuredCount = Object.keys(statusByKey).length;
    const totalCount = SUPPLIERS.length;

    return (
        <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="suppliers-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <p className="overline">Fournisseurs</p>
                    <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mt-2 flex items-center gap-3">
                        <Storefront size={32} weight="duotone" className="text-primary" />
                        Tes fournisseurs
                    </h1>
                    <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                        Vue d&apos;ensemble des sources de factures connectées à ScanBox.
                        Clique sur un fournisseur pour le configurer ou lancer un import.
                    </p>
                </div>
                <Badge variant="outline" className="text-sm" data-testid="suppliers-count">
                    {configuredCount} / {totalCount} connecté{configuredCount > 1 ? "s" : ""}
                </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {SUPPLIERS.map((s) => (
                    <SupplierCard key={s.key} supp={s} status={statusByKey[s.key]} />
                ))}
            </div>

            {/* Hint for adding more */}
            <Card className="ec-card mt-6 border-dashed bg-muted/30">
                <CardContent className="p-5 text-sm text-muted-foreground">
                    <strong className="text-foreground">À venir :</strong> Cdiscount, Fnac, intégration Gmail OAuth,
                    et autres connecteurs e-commerce. Tu peux déjà rapatrier toutes tes factures email via le
                    Connecteur mail IMAP (Outlook, Gmail, OVH, Free, etc.).
                </CardContent>
            </Card>
        </div>
    );
}
